import {
  authAtom,
  getPrimaryColor,
  hasToken,
  isMobileAtom,
  JEJURO,
  LoginModal,
  useGetReferralInfo,
  useRecoil,
} from '@orcar/common-d2c';
import { useState } from 'react';
import styled from 'styled-components';

const ORIGIN_URL = import.meta.env.VITE_ORIGIN_URL;

const ReferralPage = () => {
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const { recoilValue: user } = useRecoil(authAtom);
  const isLoggedIn = !!user || !!hasToken();

  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: referral } = useGetReferralInfo({ enabled: isLoggedIn });

  const code = referral?.code ?? '';
  // ORIGIN_URL 끝 슬래시를 제거해 '//invite' 중복 슬래시를 방지한다.
  const origin = (ORIGIN_URL ?? '').replace(/\/+$/, '');
  const inviteLink = code ? `${origin}/invite/${code}` : origin;

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 클립보드 미지원 환경 무시 */
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '제주로렌트카 친구초대',
          url: inviteLink,
        });
        return;
      } catch {
        /* 공유 취소 무시 */
      }
    }
    void copy(inviteLink);
  };

  if (!isLoggedIn) {
    return (
      <Wrapper isMobile={isMobile}>
        <SectionTitle isMobile={isMobile}>친구 초대</SectionTitle>
        <GateCard>
          <p className='t'>로그인하고 친구를 초대하세요</p>
          <p className='d'>
            친구는 첫 예약 할인(할인율에 따라 최대 5,000원), 나는 친구 첫 이용
            시 5,000P!
          </p>
          <GateButton type='button' onClick={() => setIsLoginOpen(true)}>
            로그인
          </GateButton>
        </GateCard>
        <LoginModal
          platform={isMobile ? 'mobile' : 'desktop'}
          isOpen={isLoginOpen}
          originUrl={ORIGIN_URL}
          onClose={() => setIsLoginOpen(false)}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper isMobile={isMobile}>
      <SectionTitle isMobile={isMobile}>친구 초대</SectionTitle>

      <HeroCard>
        <span className='label'>내 추천코드</span>
        <span className='code'>{code || '••••••••'}</span>
        <CopyButton type='button' onClick={() => copy(code)}>
          {copied ? '복사됨!' : '코드 복사'}
        </CopyButton>
      </HeroCard>

      <ShareButton type='button' onClick={share}>
        초대 링크 공유하기
      </ShareButton>

      <Stats>
        <div className='stat'>
          <span className='k'>초대한 친구</span>
          <span className='v'>
            {(referral?.invitedCount ?? 0).toLocaleString()}명
          </span>
        </div>
        <div className='stat'>
          <span className='k'>받은 포인트</span>
          <span className='v accent'>
            {(referral?.totalRewardPoint ?? 0).toLocaleString()}P
          </span>
        </div>
      </Stats>

      <Steps>
        <li>
          <b>1</b> 친구에게 내 추천코드를 공유해요.
        </li>
        <li>
          <b>2</b> 친구가 가입/결제 시 코드를 입력하면{' '}
          <em>할인율에 따라 첫 예약 할인(최대 5,000원)</em>.
        </li>
        <li>
          <b>3</b> 친구가 첫 예약을 이용완료하면 나에게 <em>5,000P</em> 적립!
        </li>
      </Steps>

      <Notice>
        적립 포인트는 친구의 첫 예약 반납완료 시 지급되며, 적립일로부터 12개월간
        사용할 수 있어요. 제주 법 개정으로 차량 할인은 최대 60%까지만 적용되어,
        추천 할인도 이 범위 안에서 적용돼요.
      </Notice>
    </Wrapper>
  );
};

export default ReferralPage;

const Wrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isMobile }) => (isMobile ? '100%' : '792px')};
  padding: ${({ isMobile }) => (isMobile ? '23px 20px 40px' : '0 0 40px')};
`;

const SectionTitle = styled.p<{ isMobile?: boolean }>`
  margin-bottom: 20px;
  ${({ isMobile, theme }) =>
    isMobile ? theme.NEW_FONT.H4 : theme.NEW_FONT.H3};
  font-weight: 700;
`;

const HeroCard = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 24px;
  border-radius: 18px;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: linear-gradient(135deg, ${getPrimaryColor(JEJURO)}, #0077c0);

  .label {
    ${({ theme }) => theme.FONT.B3};
    opacity: 0.9;
  }
  .code {
    ${({ theme }) => theme.FONT.H1};
    font-weight: 800;
    letter-spacing: 0.12em;
  }
`;

const CopyButton = styled.button`
  margin-top: 6px;
  padding: 9px 20px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  color: ${getPrimaryColor(JEJURO)};
  background: ${({ theme }) => theme.COLOR.WHITE};
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;
`;

const ShareButton = styled.button`
  margin-top: 12px;
  padding: 15px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
`;

const Stats = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;

  .stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 16px 18px;
    border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
    border-radius: 14px;
  }
  .k {
    ${({ theme }) => theme.FONT.B3};
    color: ${({ theme }) => theme.COLOR.GREY_02};
  }
  .v {
    ${({ theme }) => theme.FONT.H4};
    font-weight: 800;
  }
  .v.accent {
    color: ${getPrimaryColor(JEJURO)};
  }
`;

const Steps = styled.ul`
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    ${({ theme }) => theme.FONT.B2};
    color: ${({ theme }) => theme.COLOR.GREY_01};
  }
  li b {
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: ${({ theme }) => theme.COLOR.GREY_BG};
    color: ${getPrimaryColor(JEJURO)};
    ${({ theme }) => theme.FONT.B3};
    font-weight: 800;
  }
  li em {
    font-style: normal;
    font-weight: 700;
    color: ${getPrimaryColor(JEJURO)};
  }
`;

const GateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  border-radius: 18px;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
  text-align: center;

  .t {
    margin: 0;
    ${({ theme }) => theme.FONT.B1};
    font-weight: 700;
  }
  .d {
    margin: 0;
    ${({ theme }) => theme.FONT.B3};
    color: ${({ theme }) => theme.COLOR.GREY_02};
    word-break: keep-all;
  }
`;

const GateButton = styled.button`
  margin-top: 12px;
  padding: 12px 32px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
`;

const Notice = styled.p`
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  word-break: keep-all;
`;
